import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Copy, Trash2, Shield } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { roleService, menuService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast, Input } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';
import { ACTION_COLS } from '../../constants';

const RoleManagement = () => {
  const { dark } = useTheme();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('roles', 'create');
  const canEdit = hasPermission('roles', 'update');
  const canDelete = hasPermission('roles', 'delete');

  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [permRole, setPermRole] = useState(null);
  const [permData, setPermData] = useState({});
  const [cloneRole, setCloneRole] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const [form, setForm] = useState({ name: '', code: '', description: '', color: '#6366F1' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([roleService.getAll(), menuService.getAll()]);
      setRoles(r.data.data || []);
      setMenus(m.data.data || []);
    } catch { setToast({ msg: 'Error loading roles', type: 'error' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openEdit = (role) => {
    setEditRole(role);
    setForm({ name: role.name, code: role.code, description: role.description || '', color: role.color || '#6366F1' });
    setFormOpen(true);
  };

  const openCreate = () => {
    setEditRole(null);
    setForm({ name: '', code: '', description: '', color: '#6366F1' });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editRole) await roleService.update(editRole._id, form);
      else await roleService.create(form);
      setToast({ msg: editRole ? 'Role updated' : 'Role created', type: 'success' });
      setFormOpen(false);
      fetch();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error', type: 'error' });
    }
  };

  const openPerms = (role) => {
    setPermRole(role);
    const pd = {};
    (role.permissions || []).forEach(p => {
      pd[p.menu?.code || p.menuCode] = pd[p.menu?.code || p.menuCode] || {};
      p.actions?.forEach(a => { pd[p.menu?.code || p.menuCode][a] = true; });
    });
    setPermData(pd);
  };

  const togglePerm = (menuCode, action) => {
    setPermData(prev => ({
      ...prev,
      [menuCode]: { ...(prev[menuCode] || {}), [action]: !prev[menuCode]?.[action] },
    }));
  };

  const handleSavePerms = async () => {
    const permissions = menus.map(m => ({
      menu: m._id,
      menuCode: m.code,
      actions: ACTION_COLS.filter(a => permData[m.code]?.[a]),
    }));
    try {
      await roleService.updatePermissions(permRole._id, { permissions });
      setToast({ msg: 'Permissions updated', type: 'success' });
      setPermRole(null);
      fetch();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error updating permissions', type: 'error' });
    }
  };

  const handleClone = async () => {
    if (!cloneName.trim()) return;
    try {
      await roleService.clone(cloneRole._id, { name: cloneName });
      setToast({ msg: 'Role cloned', type: 'success' });
      setCloneRole(null);
      setCloneName('');
      fetch();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error cloning role', type: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await roleService.delete(deleteId);
      setToast({ msg: 'Role deleted', type: 'success' });
      setDeleteId(null);
      fetch();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error deleting role', type: 'error' });
    }
  };

  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16, overflow: 'hidden' };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Roles & Permissions</h1>
        {canCreate && <Button icon={Plus} onClick={openCreate}>Add Role</Button>}
      </div>

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          {loading ? <div style={{ padding: 20 }}><Skeleton rows={4} cols={4} /></div> :
            roles.length === 0 ? <div style={{ padding: 40 }}><EmptyState title="No roles found" /></div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                  <tr>
                    <th style={thStyle(dark)}>Role Name</th>
                    <th style={thStyle(dark)}>Code</th>
                    <th style={thStyle(dark)}>Description</th>
                    <th style={thStyle(dark)}>Menus</th>
                    <th style={thStyle(dark)}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(r => (
                    <tr key={r._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={tdStyle(dark)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 3, background: r.color || '#6366F1', flexShrink: 0 }} />
                          <span style={{ fontWeight: 600 }}>{r.name}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle(dark), fontFamily: 'monospace', fontSize: 11 }}>{r.code}</td>
                      <td style={{ ...tdStyle(dark), color: dark ? '#64748B' : '#94A3B8' }}>{r.description || '—'}</td>
                      <td style={tdStyle(dark)}><Badge color="#6366F1">{r.menus?.length || 0} menus</Badge></td>
                      <td style={tdStyle(dark)}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button title="Permissions" style={iconBtnStyle(dark)} onClick={() => openPerms(r)}><Shield size={14} /></button>
                          {canEdit && <button title="Edit" style={iconBtnStyle(dark)} onClick={() => openEdit(r)}><Pencil size={14} /></button>}
                          {canCreate && <button title="Clone" style={iconBtnStyle(dark)} onClick={() => { setCloneRole(r); setCloneName(`${r.name} (Copy)`); }}><Copy size={14} /></button>}
                          {canDelete && !r.isSystem && <button title="Delete" style={{ ...iconBtnStyle(dark), color: '#EF4444' }} onClick={() => setDeleteId(r._id)}><Trash2 size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>

      {/* Create / Edit Role */}
      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editRole ? 'Edit Role' : 'Create Role'} size="md">
        <form onSubmit={handleSubmit}>
          <Input label="Role Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sales Manager" />
          <Input label="Code *" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. SALES_MANAGER" />
          <Input label="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: dark ? '#94A3B8' : '#64748B', display: 'block', marginBottom: 6 }}>Color</label>
            <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 40, height: 36, borderRadius: 6, border: 'none', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="outline" type="button" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit">{editRole ? 'Save Changes' : 'Create Role'}</Button>
          </div>
        </form>
      </Modal>

      {/* Permissions Matrix */}
      <Modal open={!!permRole} onClose={() => setPermRole(null)} title={`Permissions — ${permRole?.name}`} size="xl">
        {permRole && (
          <div>
            <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: dark ? '#0F172A' : '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={thStyle(dark)}>Module</th>
                    {ACTION_COLS.map(a => <th key={a} style={{ ...thStyle(dark), textTransform: 'capitalize' }}>{a}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {menus.map(m => (
                    <tr key={m._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                      <td style={{ ...tdStyle(dark), fontWeight: 600, fontSize: 12 }}>{m.name}</td>
                      {ACTION_COLS.map(a => (
                        <td key={a} style={{ ...tdStyle(dark), textAlign: 'center' }}>
                          <input type="checkbox" checked={!!permData[m.code]?.[a]} onChange={() => togglePerm(m.code, a)} style={{ cursor: 'pointer', width: 15, height: 15 }} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="outline" onClick={() => setPermRole(null)}>Cancel</Button>
              <Button onClick={handleSavePerms}>Save Permissions</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Clone */}
      <Modal open={!!cloneRole} onClose={() => setCloneRole(null)} title="Clone Role" size="sm">
        <Input label="New Role Name" value={cloneName} onChange={e => setCloneName(e.target.value)} placeholder="Enter name for the cloned role" />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="outline" onClick={() => setCloneRole(null)}>Cancel</Button>
          <Button onClick={handleClone}>Clone</Button>
        </div>
      </Modal>

      {/* Delete */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Confirm Delete" size="sm">
        <p style={{ fontSize: 14, color: dark ? '#CBD5E1' : '#334155', margin: '0 0 20px' }}>Are you sure you want to delete this role?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
};

export default RoleManagement;
